'use client';

import { GetActivitiesParams, GetActivitiesStatusFilter } from '@api/models';
import { useMemo } from 'react';

import { MAX_KEYWORD_QUERY_LENGTH, MIN_KEYWORD_QUERY_LENGTH } from '../components/KeywordSuggestionsDropdown';
import { SEARCH_PARAMS } from '../constants/searchParams';
import { MapBounds } from '../utils/mapUtils';
import { useSyncSearchParams } from './useSyncSearchParams';

export function useSearchApiParams(mapBounds: MapBounds | null): GetActivitiesParams {
  const syncParams = useSyncSearchParams();

  return useMemo((): GetActivitiesParams => {
    // API contract: 3-80 chars. Shorter values are treated as "no keyword"
    // instead of letting a hand-edited URL turn into a 400.
    const rawQuery = (syncParams.get(SEARCH_PARAMS.QUERY) || '').trim().slice(0, MAX_KEYWORD_QUERY_LENGTH);
    const query = rawQuery.length >= MIN_KEYWORD_QUERY_LENGTH ? rawQuery : undefined;
    const categoryId = syncParams.get(SEARCH_PARAMS.CATEGORY_ID) || undefined;
    const isAtHomeRaw = syncParams.get(SEARCH_PARAMS.IS_AT_HOME);
    const firstIsAtHome = isAtHomeRaw?.split(',').filter((v) => v !== '')[0];
    const isAtHome = firstIsAtHome ? firstIsAtHome === 'true' : undefined;
    const dateRange = syncParams.get(SEARCH_PARAMS.DATE) || undefined;
    const timeOfDay = syncParams.get(SEARCH_PARAMS.SLOT_TIME) || undefined;
    const dateFrom = syncParams.get(SEARCH_PARAMS.DATE_FROM);
    const dateTo = syncParams.get(SEARCH_PARAMS.DATE_TO);
    const limitStr = syncParams.get(SEARCH_PARAMS.LIMIT);
    const cursor = syncParams.get(SEARCH_PARAMS.CURSOR) || undefined;

    return {
      query,
      categoryId,
      isAtHome,
      dateRange,
      timeOfDay,
      startDate: dateFrom ? dateFrom.slice(0, 10) : undefined,
      endDate: dateTo ? dateTo.slice(0, 10) : undefined,
      limit: limitStr ? parseInt(limitStr, 10) : undefined,
      cursor,
      statusFilter: GetActivitiesStatusFilter.approved,
      includeFallback: true,
      northEastLat: mapBounds?.neLat,
      northEastLng: mapBounds?.neLng,
      southWestLat: mapBounds?.swLat,
      southWestLng: mapBounds?.swLng
    };
  }, [syncParams, mapBounds]);
}
